import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { apiService } from '../services/api';
import { FiDownload, FiFile, FiSearch, FiChevronUp, FiChevronDown, FiCheckCircle, FiClock, FiX } from 'react-icons/fi';

interface CV {
  id: number;
  filename_original: string;
  filename_stored: string;
  storage_path: string;
  download_url: string;
  mime: string;
  size_bytes: number;
  size_mb: number;
  status: string;
  is_analyzed: boolean;
  is_valid_cv: boolean;
  score: number | null;
  skills_score: number | null;
  experience_score: number | null;
  education_score: number | null;
  language_score: number | null;
  recommendation: string | null;
  candidate_name: string | null;
  email: string | null;
  phone: string | null;
  age: number | null;
  years_of_experience: number | null;
  candidate_location: string | null;
  created_at: string;
  analyzed_at: string | null;
  upload_type: string;
  contact_extracted: boolean;
  user: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    organization: string;
  } | null;
  job: {
    id: number;
    title: string;
    status: string;
  } | null;
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

interface Job {
  id: number;
  title: string;
}

const CVs: React.FC = () => {
  const [cvs, setCVs] = useState<CV[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [stats, setStats] = useState({
    total: 0,
    analyzed: 0,
    pending: 0,
    notCV: 0
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterUser, setFilterUser] = useState<number | string>('all');
  const [filterJob, setFilterJob] = useState<number | string>('all');
  const [orderBy, setOrderBy] = useState('-created_at');

  const [users, setUsers] = useState<User[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [jobSearchTerm, setJobSearchTerm] = useState('');

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const fetchCVs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getCVs({
        page: currentPage,
        page_size: pageSize,
        search: searchQuery,
        status: filterStatus,
        user_id: filterUser,
        job_id: filterJob,
        order_by: orderBy,
      });

      if (response && response.success && response.data) {
        setCVs(response.data.results);
        setTotalPages(response.data.total_pages);
        setTotalCount(response.data.count);
        
        // Use backend-provided stats for accurate counts across all pages
        const responseData = response.data as any;
        if (responseData.stats) {
          setStats({
            total: responseData.stats.total || 0,
            analyzed: responseData.stats.analyzed || 0,
            pending: responseData.stats.pending || 0,
            notCV: responseData.stats.not_cv || 0
          });
        }
      }
    } catch (error) {
      console.error('Error fetching CVs:', error);
      setToast({ message: 'Failed to load CVs. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, filterStatus, filterUser, filterJob, orderBy]);

  const fetchUsers = async () => {
    try {
      const response = await apiService.getUsers({ page_size: 1000 });
      if (response && response.success && response.data) {
        setUsers(response.data.results);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      // Fetch jobs without filters - will get all jobs
      const response = await apiService.getJobs();
      if (response && response.success && response.data) {
        const jobsList = Array.isArray(response.data) ? response.data : (response.data.results || []);
        setJobs(jobsList);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  useEffect(() => {
    fetchCVs();
  }, [fetchCVs]);

  useEffect(() => {
    fetchUsers();
    fetchJobs();
  }, []);

  const isPDF = (cv: CV) => {
    return cv.mime?.includes('pdf') || cv.filename_original?.toLowerCase().endsWith('.pdf');
  };

  const canPreview = (cv: CV) => {
    return cv.storage_path && isPDF(cv);
  };

  const handlePreviewCV = async (cv: CV) => {
    try {
      setToast({ message: 'Loading preview...', type: 'info' });
      
      const blobUrl = await apiService.getCVBlobUrl(cv.id, true);
      window.open(blobUrl, '_blank');
      
      setToast({ message: '', type: 'success' });
    } catch (error: any) {
      console.error('Preview failed:', error);
      setToast({ 
        message: error.message || 'Failed to preview CV', 
        type: 'error' 
      });
    }
  };

  const handleDownloadCV = async (cv: CV) => {
    try {
      setToast({ message: 'Downloading...', type: 'info' });
      
      const blobUrl = await apiService.getCVBlobUrl(cv.id, false);
      
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = cv.filename_original || 'cv.pdf';
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
      
      setToast({ 
        message: 'CV downloaded successfully', 
        type: 'success' 
      });
    } catch (error: any) {
      console.error('Download failed:', error);
      setToast({ 
        message: error.message || 'Failed to download CV', 
        type: 'error' 
      });
    }
  };

  const handleFileClick = async (cv: CV, e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!cv.storage_path) {
      setToast({ message: 'File not found or deleted', type: 'error' });
      return;
    }
    
    if (canPreview(cv)) {
      await handlePreviewCV(cv);
    } else {
      await handleDownloadCV(cv);
    }
  };

  const handleSort = (field: string) => {
    if (orderBy === field) {
      setOrderBy(`-${field}`);
    } else if (orderBy === `-${field}`) {
      setOrderBy(field);
    } else {
      setOrderBy(field);
    }
    setCurrentPage(1);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      uploaded: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', icon: <FiClock /> },
      queued: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300', icon: <FiClock /> },
      analyzing: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300', icon: <FiClock /> },
      analyzed: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300', icon: <FiCheckCircle /> },
      failed: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300', icon: <FiX /> },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.uploaded;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) {
      return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
  };

  const truncateFilename = (filename: string, maxLength: number = 30) => {
    if (filename.length <= maxLength) return filename;
    return filename.substring(0, maxLength) + '...';
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const filteredJobs = jobs.filter(job =>
    job.title.toLowerCase().includes(jobSearchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white mb-2">CV Uploads</h1>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-navy-800 rounded-lg p-4 shadow">
            <p className="text-sm text-gray-600 dark:text-gray-400">Total CVs</p>
            <p className="text-2xl font-bold text-navy-700 dark:text-white">{stats.total}</p>
          </div>
          <div className="bg-white dark:bg-navy-800 rounded-lg p-4 shadow">
            <p className="text-sm text-gray-600 dark:text-gray-400">Analyzed</p>
            <p className="text-2xl font-bold text-green-600">{stats.analyzed}</p>
          </div>
          <div className="bg-white dark:bg-navy-800 rounded-lg p-4 shadow">
            <p className="text-sm text-gray-600 dark:text-gray-400">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="bg-white dark:bg-navy-800 rounded-lg p-4 shadow">
            <p className="text-sm text-gray-600 dark:text-gray-400">Not CVs</p>
            <p className="text-2xl font-bold text-red-600">{stats.notCV}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-navy-800 rounded-lg p-6 shadow mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search - Takes more space */}
            <div className="flex-1">
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search by filename, name, or email..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full h-12 pl-12 pr-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-navy-700 text-navy-700 dark:text-white placeholder-gray-400 focus:border-brand-500 focus:ring-0 transition-colors"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="w-full lg:w-48">
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full h-12 px-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:border-brand-500 focus:ring-0 transition-colors cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="uploaded">Uploaded</option>
                <option value="queued">Queued</option>
                <option value="analyzing">Analyzing</option>
                <option value="analyzed">Analyzed</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {/* User Filter */}
            <div className="w-full lg:w-64">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full h-10 px-3 mb-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-navy-900 text-navy-700 dark:text-white placeholder-gray-400 focus:border-brand-500 focus:ring-0"
                />
                <select
                  value={filterUser}
                  onChange={(e) => {
                    setFilterUser(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full h-12 px-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:border-brand-500 focus:ring-0 transition-colors cursor-pointer"
                >
                  <option value="all">All Users</option>
                  {filteredUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.email}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Job Filter */}
            <div className="w-full lg:w-64">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={jobSearchTerm}
                  onChange={(e) => setJobSearchTerm(e.target.value)}
                  className="w-full h-10 px-3 mb-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-navy-900 text-navy-700 dark:text-white placeholder-gray-400 focus:border-brand-500 focus:ring-0"
                />
                <select
                  value={filterJob}
                  onChange={(e) => {
                    setFilterJob(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full h-12 px-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:border-brand-500 focus:ring-0 transition-colors cursor-pointer"
                >
                  <option value="all">All Jobs</option>
                  {filteredJobs.map(job => (
                    <option key={job.id} value={job.id}>
                      {job.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Page Size */}
            <div className="w-full lg:w-40">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="w-full h-12 px-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-navy-700 text-navy-700 dark:text-white focus:border-brand-500 focus:ring-0 transition-colors cursor-pointer"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-navy-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-navy-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Filename
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-800"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      Uploaded At
                      {orderBy === 'created_at' && <FiChevronUp />}
                      {orderBy === '-created_at' && <FiChevronDown />}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-800"
                    onClick={() => handleSort('analyzed_at')}
                  >
                    <div className="flex items-center gap-1">
                      Analyzed At
                      {orderBy === 'analyzed_at' && <FiChevronUp />}
                      {orderBy === '-analyzed_at' && <FiChevronDown />}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Job
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-800"
                    onClick={() => handleSort('size_bytes')}
                  >
                    <div className="flex items-center gap-1">
                      Size
                      {orderBy === 'size_bytes' && <FiChevronUp />}
                      {orderBy === '-size_bytes' && <FiChevronDown />}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      Loading CVs...
                    </td>
                  </tr>
                ) : cvs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      No CVs found
                    </td>
                  </tr>
                ) : (
                  cvs.map(cv => (
                    <tr key={cv.id} className="hover:bg-gray-50 dark:hover:bg-navy-700">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FiFile className="text-gray-400 flex-shrink-0" />
                          {cv.storage_path ? (
                            <button
                              onClick={(e) => handleFileClick(cv, e)}
                              className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300 hover:underline font-medium transition-colors text-left"
                              title={`${cv.filename_original} - Click to ${canPreview(cv) ? 'preview' : 'download'}`}
                            >
                              {truncateFilename(cv.filename_original)}
                            </button>
                          ) : (
                            <div className="text-sm text-gray-400 dark:text-gray-500" title={`${cv.filename_original} - File not found`}>
                              {truncateFilename(cv.filename_original)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-300">
                        {formatDate(cv.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-300">
                        {formatDate(cv.analyzed_at)}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(cv.status)}
                      </td>
                      <td className="px-4 py-3">
                        {cv.user ? (
                          <div className="text-sm">
                            <div className="text-navy-700 dark:text-white">{cv.user.first_name} {cv.user.last_name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{cv.user.email}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {cv.job ? (
                          <div className="text-sm max-w-xs truncate text-navy-700 dark:text-white" title={cv.job.title}>
                            {cv.job.title}
                          </div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-300">
                        {formatSize(cv.size_bytes)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 bg-gray-50 dark:bg-navy-900 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} CVs
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-navy-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-navy-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-4 right-4 z-50">
            <div className={`px-6 py-3 rounded-lg shadow-lg ${
              toast.type === 'success' ? 'bg-green-500' :
              toast.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
            } text-white`}>
              {toast.message}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CVs;
